import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  BarChart3,
  Building2,
  History,
  CalendarCheck,
  CalendarRange,
  ChevronRight,
  ClipboardList,
  Circle,
  Database,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquareWarning,
  Plug,
  Rocket,
  ScrollText,
  Settings,
  ShieldAlert,
  Smartphone,
  Split,
  Tags,
  UserCircle,
  UserCog,
  Users,
  UsersRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ETIQUETA_PERFIL, navegacionPara, perfiles, type ItemNav } from '../auth/capacidades';
import { Logo } from './Marca';

/** Íconos de la navegación (lucide, mismo set que Suel2 Nómina). */
const ICONO_RUTA: Record<string, LucideIcon> = {
  '/': LayoutDashboard,
  '/onboarding': Rocket,
  '/registrar-tarja': ClipboardList,
  '/jornadas': CalendarCheck,
  '/eventos': Activity,
  '/cuarentena': ShieldAlert,
  '/repartos': Split,
  '/reclamos': MessageSquareWarning,
  '/periodos': CalendarRange,
  '/nomina': FileText,
  '/reportes': BarChart3,
  '/mi-gente': Users,
  '/maestros': Database,
  '/tarifarios': Tags,
  '/auditoria': ScrollText,
  '/integraciones': Plug,
  '/dispositivos': Smartphone,
  '/equipo': UserCog,
  '/configuracion': Settings,
  '/plataforma/organizaciones': Building2,
  '/plataforma/usuarios': UsersRound,
  '/plataforma/bitacora': History,
  '/mi-cuenta': UserCircle,
};

function SelectorTenant() {
  const { usuario, tenantActivo, cambiarTenant } = useAuth();
  const id = useId();
  const [cambiando, setCambiando] = useState(false);
  if (!usuario) return null;
  if (usuario.tenants.length < 2) {
    const actual = usuario.tenants.find((t) => t.tenant_id === tenantActivo) ?? usuario.tenants[0];
    // /auth/me puede traer solo el id: sin nombre real no se muestra un UUID suelto.
    return actual && actual.nombre !== actual.tenant_id ? <p className="w-lateral__tenant">{actual.nombre}</p> : null;
  }
  return (
    <div className="w-tenant">
      <label htmlFor={id} className="t-sr-only">
        Organización activa
      </label>
      <select
        id={id}
        value={tenantActivo ?? ''}
        disabled={cambiando}
        onChange={async (e) => {
          setCambiando(true);
          try {
            await cambiarTenant(e.target.value);
          } finally {
            setCambiando(false);
          }
        }}
      >
        {usuario.tenants.map((t) => (
          <option key={t.tenant_id} value={t.tenant_id}>
            {t.nombre}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Layout() {
  const { usuario, caps, logout, marca } = useAuth();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => setMenuAbierto(false), [pathname]);
  useEffect(() => {
    if (!menuAbierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuAbierto(false);
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [menuAbierto]);
  const items = navegacionPara(caps);
  const grupos = items.reduce<Record<string, ItemNav[]>>((acc, i) => {
    (acc[i.grupo] ??= []).push(i);
    return acc;
  }, {});
  const rolOrg = usuario?.tenant_id ? perfiles(usuario).map((p) => ETIQUETA_PERFIL[p]).join(' · ') : '';
  const rol = usuario?.admin_plataforma ? (rolOrg ? `${rolOrg} · Plataforma` : 'Administrador de plataforma') : rolOrg;
  const producto = marca.nombre_producto ?? 'Tarja';
  const inicial = usuario?.nombre?.trim().charAt(0).toUpperCase() ?? '?';

  return (
    <div className="w-app">
      <a href="#contenido" className="t-skip">
        Saltar al contenido
      </a>

      {/* Barra superior solo en pantallas angostas (la lateral pasa a cajón) */}
      <header className="w-barra-movil">
        <button type="button" className="w-menu-btn" aria-expanded={menuAbierto} aria-controls="navegacion" onClick={() => setMenuAbierto((v) => !v)}>
          {menuAbierto ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          <span className="t-sr-only">Menú</span>
        </button>
        <Logo nombre={producto} logoUrl={marca.logo_url} />
        <span className="w-barra-movil__marca">{producto}</span>
      </header>

      <aside id="navegacion" className={`w-lateral${menuAbierto ? ' w-lateral--abierto' : ''}`}>
        <div className="w-lateral__marca">
          <Logo nombre={producto} logoUrl={marca.logo_url} />
          <div className="w-lateral__marca-texto">
            <p className="w-marca">{producto}</p>
            <SelectorTenant />
          </div>
        </div>

        <nav className="w-nav" aria-label="Principal">
          {Object.entries(grupos).map(([grupo, lista]) => (
            <div key={grupo} className="w-nav__grupo">
              <h2 className="w-nav__titulo">{grupo}</h2>
              <ul>
                {lista.map((i) => {
                  const Icono = ICONO_RUTA[i.ruta] ?? Circle;
                  const activa = i.ruta === '/' ? pathname === '/' : pathname === i.ruta || pathname.startsWith(`${i.ruta}/`);
                  return (
                    <li key={i.ruta}>
                      <Link
                        to={i.ruta}
                        className="w-nav__link"
                        activeProps={{ className: 'w-nav__link w-nav__link--activo', 'aria-current': 'page' }}
                        activeOptions={{ exact: i.ruta === '/' }}
                      >
                        <Icono size={18} className="w-nav__icono" aria-hidden="true" />
                        <span className="w-nav__texto">{i.etiqueta}</span>
                        {activa ? <ChevronRight size={14} className="w-nav__chevron" aria-hidden="true" /> : null}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="w-usuario">
          <span className="w-usuario__avatar" aria-hidden="true">
            {inicial}
          </span>
          <div className="w-usuario__datos">
            <span className="w-usuario__nombre">{usuario?.nombre}</span>
            {rol ? <span className="w-usuario__rol">{rol}</span> : null}
          </div>
          <Link to="/mi-cuenta" className="w-usuario__accion" title="Mi cuenta">
            <UserCircle size={16} aria-hidden="true" />
            <span className="t-sr-only">Mi cuenta</span>
          </Link>
          <button type="button" className="w-usuario__accion w-usuario__accion--salir" title="Cerrar sesión" onClick={() => void logout()}>
            <LogOut size={15} aria-hidden="true" />
            <span className="t-sr-only">Salir</span>
          </button>
        </div>
      </aside>

      {menuAbierto ? <div className="w-velo" aria-hidden="true" onClick={() => setMenuAbierto(false)} /> : null}

      <div className="w-cuerpo">
        <main id="contenido" className="w-main" tabIndex={-1}>
          <Outlet />
        </main>
        {marca.pie_legal ? <footer className="w-pie">{marca.pie_legal}</footer> : null}
      </div>
    </div>
  );
}
